import { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { ChevronUp, Check, X, Pin, Archive, MessageSquare, CheckCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/apiClient";
import { useSSE } from "@/lib/useSSE";
import { EmptyState } from "@/components/dashboard/primitives";

const FILTERS = ["all", "pending", "approved", "answered", "rejected", "archived"];
const statusStyle = {
  pending: "bg-muted text-muted-foreground", approved: "bg-chart-3/10 text-chart-3",
  answered: "bg-accent/10 text-accent", rejected: "bg-destructive/10 text-destructive",
  archived: "bg-muted text-muted-foreground",
};

export default function EventQuestions() {
  const { event } = useOutletContext();
  const [questions, setQuestions] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = useCallback(() => {
    api.get(`/events/${event.id}/questions`).then(({ data }) => setQuestions(data)).catch(() => {});
  }, [event.id]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  useSSE(`/stream/event/${event.id}`, (m) => { if (m.type === "questions") load(); });

  const act = async (qid, body) => { await api.put(`/events/${event.id}/questions/${qid}`, body); load(); };
  const remove = async (qid) => { await api.delete(`/events/${event.id}/questions/${qid}`); toast.success("Deleted"); load(); };

  const counts = FILTERS.reduce((acc, f) => { acc[f] = f === "all" ? questions.length : questions.filter((q) => q.status === f).length; return acc; }, {});
  const shown = filter === "all" ? questions : questions.filter((q) => q.status === filter);

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h2 className="font-display text-lg font-bold text-foreground">Moderation <span className="text-muted-foreground font-normal text-sm">· live</span></h2>
        <div className="flex gap-1 flex-wrap">
          {FILTERS.map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
              className={`text-xs font-medium px-3 py-1.5 rounded-full border capitalize transition-colors ${filter === f ? "border-accent bg-accent/10 text-accent" : "border-border text-muted-foreground hover:text-foreground"}`}>
              {f} <span className="opacity-60">{counts[f]}</span>
            </button>
          ))}
        </div>
      </div>

      {shown.length === 0 ? (
        <EmptyState icon={MessageSquare} title="No questions here" description="Questions submitted by attendees appear here in real time, ranked by upvotes." />
      ) : (
        <div className="space-y-3" data-testid="questions-list">
          {shown.map((q) => (
            <div key={q.id} className={`rounded-xl border bg-card p-4 flex items-start gap-4 ${q.pinned ? "border-accent ring-1 ring-accent/30" : "border-border"}`} data-testid={`question-${q.id}`}>
              <div className="flex flex-col items-center rounded-md border border-border px-2.5 py-1.5 min-w-[3rem]">
                <ChevronUp className="h-4 w-4 text-accent" /><span className="font-display font-bold text-foreground">{q.upvotes}</span>
              </div>
              <div className="flex-1 min-w-0">
                {q.pinned && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-accent mb-1"><Pin className="h-3 w-3" /> PINNED</span>}
                <p className="text-foreground">{q.text}</p>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{q.is_anonymous ? "Anonymous" : q.author_name || "Attendee"}</span>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize ${statusStyle[q.status]}`}>{q.status}</span>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <IconBtn onClick={() => act(q.id, { status: "approved" })} title="Approve" testid={`approve-${q.id}`}><Check className="h-4 w-4" /></IconBtn>
                <IconBtn onClick={() => act(q.id, { status: "answered" })} title="Mark answered" testid={`answer-${q.id}`}><CheckCheck className="h-4 w-4" /></IconBtn>
                <IconBtn onClick={() => act(q.id, { pinned: !q.pinned })} title="Pin" testid={`pin-${q.id}`} active={q.pinned}><Pin className="h-4 w-4" /></IconBtn>
                <IconBtn onClick={() => act(q.id, { status: "rejected" })} title="Reject"><X className="h-4 w-4" /></IconBtn>
                <IconBtn onClick={() => act(q.id, { status: "archived" })} title="Archive"><Archive className="h-4 w-4" /></IconBtn>
                <IconBtn onClick={() => remove(q.id)} title="Delete" danger><Trash2 className="h-4 w-4" /></IconBtn>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function IconBtn({ children, onClick, title, testid, active, danger }) {
  return (
    <button onClick={onClick} title={title} data-testid={testid}
      className={`h-8 w-8 rounded-md flex items-center justify-center transition-colors ${active ? "bg-accent/10 text-accent" : danger ? "text-muted-foreground hover:bg-destructive/10 hover:text-destructive" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}>
      {children}
    </button>
  );
}
